import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { SelectorReturnTypeFor } from '@frontend/sports/common/core/utils/redux';
import { ThemeSize } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest, map } from 'rxjs';

import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../../base/store/state';
import { BetslipType } from '../../../../../core/betslip-type';
import { BetslipOnboardingMessageService } from '../../../../onboarding/betslip-onboarding-message.service';
import { selectPicksCountInfo } from '../../../../picks/selectors';
import { teaserBetStateSelector } from '../../../../teaser-bet/selectors';
import { BetslipTypesActions } from '../../../actions';
import { betslipTypeStateSelector } from '../../../selectors';
import { IBetslipTypeState } from '../../../state';

const teaserMinPicksCount = 2;

function checkMinPicksCountForTeasers(picksCount: number): boolean {
    return picksCount >= teaserMinPicksCount;
}

@Component({
    selector: 'bs-betslip-type-tabs',
    templateUrl: './betslip-type-tabs.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./betslip-type-tabs.component.scss'],
    standalone: false,
})
export default class BetslipTypeTabsComponent implements OnInit, OnDestroy {
    @Input() isVisible: boolean;
    @Input() selectedBetslipType: BetslipType | null;
    @Input() activeBetslipTypes: BetslipType[];
    @HostBinding('class') className = 'betslip-type-tabs';

    isTeaserBetTabEnabled = false;

    readonly messages: { [key in BetslipType]: string };

    private readonly minComboPicks: number;
    private readonly minSystemPicks: number;
    private teaserBetStateSelectorSubscription: Subscription;
    tabs$: Observable<{ type: BetslipType; isEnabled: boolean; title: string }[]>;
    betslipTypeState: IBetslipTypeState;
    betBuilderPicksState: SelectorReturnTypeFor<typeof selectPicksCountInfo>;
    themedSize: ThemeSize;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private betslipConfig: BetslipConfig,
        private changeDetectorRef: ChangeDetectorRef,
        private store: Store<IBetslipRootState>,
        private betslipOnboardingMessageService: BetslipOnboardingMessageService,
        device: DeviceService,
    ) {
        const betTypes = messageProvider.getMessages().betTypes;
        this.messages = {
            [BetslipType.Single]: betTypes.Single,
            [BetslipType.Combo]: betTypes.Combo,
            [BetslipType.Teaser]: betTypes.Teaser,
            [BetslipType.System]: betTypes.System,
            [BetslipType.EditBet]: '',
            [BetslipType.BetBuilder]: '',
        };
        this.minComboPicks = betslipConfig.minComboBets == null ? 2 : betslipConfig.minComboBets;
        this.minSystemPicks = betslipConfig.minSystemBets == null ? 3 : betslipConfig.minSystemBets;

        this.tabs$ = combineLatest([this.store.select(betslipTypeStateSelector), this.store.select(selectPicksCountInfo)]).pipe(
            map(([typeState, betBuilderPicksState]) => {
                this.betslipTypeState = typeState;
                this.betBuilderPicksState = betBuilderPicksState;

                return this.activeBetslipTypes.map((type) => {
                    const isEnabled = this.isTypeEnabled(type);

                    return { type, isEnabled, title: this.messages[type] };
                });
            }),
        );
        this.themedSize = {
            'mgm-4': device.isMobile ? 'large' : 'small',
            'sports-interaction': device.isMobile ? 'large' : 'small',
            'betboo-br': 'small',
            'sportingbet-br': 'small',
        };
    }

    ngOnInit(): void {
        this.teaserBetStateSelectorSubscription = this.store
            .select(teaserBetStateSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((teaserState) => {
                this.isTeaserBetTabEnabled = checkMinPicksCountForTeasers(Object.entries(teaserState.picks)?.length);
                this.changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.teaserBetStateSelectorSubscription.unsubscribe();
    }

    get picksCount() {
        return this.betBuilderPicksState.nonBetBuilderPicksCount + this.betBuilderPicksState.betBuilderPicksCount;
    }

    /**
     * Define if tab can be clicked and selected
     */
    isTypeEnabled(betslipType: BetslipType): boolean {
        const hasValidOnboardingMessage = () => !!this.betslipOnboardingMessageService.mapOnboardingMessage(betslipType, this.betslipTypeState);

        switch (betslipType) {
            case BetslipType.Single:
                const singlePicksCount = this.betslipConfig.isSportcastAsComboEnabled
                    ? this.betBuilderPicksState.nonBetBuilderPicksCount
                    : this.picksCount;

                return singlePicksCount > 0;
            case BetslipType.Combo:
                const betBuilderPicksCount = this.betslipConfig.isSportcastAsComboEnabled ? this.betBuilderPicksState.betBuilderPicksCount : 0;

                return this.picksCount >= this.minComboPicks || betBuilderPicksCount > 0 || hasValidOnboardingMessage();
            case BetslipType.System:
                const systemPicksCount = this.betslipConfig.isSportcastAsComboEnabled
                    ? this.betBuilderPicksState.nonBetBuilderPicksCount
                    : this.picksCount;

                return systemPicksCount >= this.minSystemPicks || hasValidOnboardingMessage();
            case BetslipType.Teaser:
                return this.isTeaserBetTabEnabled || hasValidOnboardingMessage();
            default:
                return false;
        }
    }

    /**
     * Is current tab selected
     *
     * @param betslipType
     */
    isTypeActive(betslipType: BetslipType): boolean {
        return this.selectedBetslipType === betslipType;
    }

    onSelectBetslipType(betslipType: BetslipType): void {
        if (this.isTypeEnabled(betslipType) && !this.isTypeActive(betslipType)) {
            this.store.dispatch(
                BetslipTypesActions.setSelectedType({
                    betslipType,
                    selectedByUser: true,
                    systemInfo: null,
                }),
            );
        }
    }

    isTabEnabled(betslipType: BetslipType): boolean {
        return this.isTypeActive(betslipType) || this.isTypeEnabled(betslipType);
    }
}
